import { bound } from "@frui.ts/helpers/dist";
import { Router } from "@frui.ts/screens/dist";
import * as Sentry from "@sentry/react";
import { Modules } from "config";
import type { IPagedResults } from "data/types";
import Exportable from "entities/exportable";
import InternalTransferResponse from "entities/internalTransferResponse";
import { action, computed, observable, reaction } from "mobx";
import InternalTransferRepository from "repositories/internalTransferRepository";
import ConfirmationService from "services/confirmationService";
import EventBus, { allApprovedInternalTransfersExported } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import SecurityService from "services/securityService";
import type CountrySelectViewModel from "../../countrySelectViewModel";
import ApprovalListViewModel from "../../common/approvalListViewModel";
import type InternalTransferEditViewModel from "./internalTransferEditViewModel";
import InternalTransfersRequest from "entities/internalTransfersRequest";
import InternalTransferApprovalService from "services/internalTransferApprovalService";
import InternalTransferApprovalType from "entities/internalTransferApprovalType";

const navigationName = "internal-transfers";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
  children: [],
})
export default class InternalTransfersViewModel extends ApprovalListViewModel<
  InternalTransferApprovalType,
  InternalTransferResponse,
  any,
  InternalTransfersRequest
> {
  readonly modelName = "internalTransfer";
  readonly approvalType = InternalTransferApprovalType;
  @observable readonly editViewModels = new Map<InternalTransferResponse, InternalTransferEditViewModel>();

  constructor(
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected repository: InternalTransferRepository,
    protected approvalService: InternalTransferApprovalService,
    protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>,
    protected internalTransferEditViewModelFactory: ReturnType<typeof InternalTransferEditViewModel.Factory>,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService
  ) {
    super(localizationService, eventBus, approvalService, countrySelectViewModelFactory);
    this.navigationName = navigationName;
  }

  onInitialize() {
    this.unsubscriptions.push(
      reaction(
        () => [
          this.filter.specificSymbol,
          this.filter.constantSymbol,
          this.filter.variableSymbol,
          this.filter.recipientBankAccount,
          this.filter.senderBankAccount,
          this.filter.notApprovedBy,
          this.filter.approvedBy,
        ],
        () => {
          this.filter.fullTextSearchEnabled = false;
        }
      )
    );
    this.unsubscriptions.push(this.eventBus.subscribe(allApprovedInternalTransfersExported, this.onGroupActionSuccess));
    return super.onInitialize();
  }

  @bound
  getEditViewModel(item: InternalTransferResponse) {
    return this.editViewModels.get(item);
  }

  protected resetFilterValues(filter: InternalTransfersRequest) {
    super.resetFilterValues(filter);
    if (this.filter) {
      this.filter.isExported = false;
    }
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `internalTransfers.${code}`, size);
  }

  protected async fetchData(): Promise<IPagedResults<InternalTransferResponse>> {
    const { limit, offset } = this.pagingFilter;
    const data = await this.repository.getAll({ offset, limit }, this.appliedFilter);
    const { items } = data;
    this.editViewModels.clear();
    items.forEach(item => this.editViewModels.set(item, this.internalTransferEditViewModelFactory(item)));

    return data;
  }

  protected createFilter() {
    return new InternalTransfersRequest();
  }

  @action.bound
  approveClick = (item: InternalTransferResponse) => async () => {
    if (item.amount <= 0) {
      this.notificationService.add({ title: this.translate("amountWarning"), type: NotificationType.ERROR });
      return;
    }

    await this.approvalService.approve(this.repository, item, {
      title: this.translate("confirmApproval.title"),
      confirm: this.translate("confirmApproval.confirm"),
      approved: this.translate("candidateApproved"),
    });
  };

  @computed
  get noApprovalsForExport() {
    return !this.items.some(item => !item.isExported && this.approvalService.isApprovalProcessComplete(item));
  }

  @bound
  isExported(item: Exportable): boolean {
    return item.isExported ?? false;
  }

  @bound
  async exportApproved() {
    const confirmed = await this.confirmationService.showConfirmation(
      this.translate("confirmExportApprovedInternalTransfers.title"),
      { confirmButton: { text: this.translate("confirmExportApprovedInternalTransfers.confirm"), variant: "danger" } }
    );

    if (!confirmed) {
      return;
    }

    try {
      await this.repository.export();
      this.notificationService.add({ title: this.translate("internalTransfersExported"), type: NotificationType.SUCCESS });
      this.eventBus.publish(allApprovedInternalTransfersExported());
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  @computed
  get name() {
    return this.translate("name");
  }

  translateFilterAttribute(model: "internalTransfersRequest", attribute: keyof InternalTransfersRequest) {
    return this.localizationService.translateAttribute(model, attribute);
  }

  translateIsExported(option: "exported" | "not-exported") {
    return this.translate(`isExportedOptions.${option}`);
  }
}
